import type { CapabilitiesSnapshot, MeResponse } from '../../entities/me/me-contract.types';
import { canShowNavItem, canShowSuperadminUsersHubNavItem, hasPermission, isRouteAllowed } from '../../shared/access/access';
import {
  canAccessTenantOrganizationProfileSurface,
  hasTenantWideStaffGovernanceContourOrSaOrgLift,
  isTenantOrganizationProfileStaffRouteKey,
  isTenantStaffGovernanceKeyedRoute,
  type SuperadminOrgTenantWideUiGateContext
} from '../../shared/access/tenant-staff-governance';
import { isStaffPlatformModeSidebarEntry } from '../../shared/access/staff-platform-mode';
import { resolveStaffNavSidebarSection, type StaffNavCatalogEntry, type StaffNavSectionId } from './staff-route-catalog';

/** Пользовательские заголовки групп сайдбара (используются меню и палитрой Ctrl+K). */
const STAFF_NAV_SECTION_TITLES: Record<StaffNavSectionId, string> = {
  shell: 'Общее',
  organization: 'Организация',
  users: 'Пользователи',
  service_catalog: 'Справочники',
  resources: 'Залы и оборудование',
  subscriptions: 'Абонементы',
  schedule: 'Расписание',
  payments: 'Операционные платежи',
  payment_governance: 'Приём платежей (организация)',
  application: 'Приложение',
  clients: 'Клиенты',
  specialists: 'Специалисты',
  fitness: 'Fitness',
  sales: 'Продажи',
  tasks: 'Задачи',
  assistant: 'Помощник',
  automation: 'Автоматизация',
  journal: 'Журнал',
  audit: 'Аудит',
  yclients: 'YClients',
  platform: 'Платформа',
  account: 'Аккаунт',
  access_control: 'Контроль доступа'
};

export function staffNavSectionTitle(id: StaffNavSectionId): string {
  return STAFF_NAV_SECTION_TITLES[id];
}

/** Пункты меню, видимые текущему пользователю (та же логика, что в сайдбаре). */
export function filterVisibleStaffNavItems(
  items: StaffNavCatalogEntry[],
  capabilities: CapabilitiesSnapshot | null | undefined,
  platformMode: boolean,
  saOrgWideGateCtx: SuperadminOrgTenantWideUiGateContext,
  me: Pick<MeResponse, 'role' | 'organization_id'> | null | undefined,
  impersonateOrganizationId: number | null | undefined
): StaffNavCatalogEntry[] {
  return items.filter((e) => {
    if (platformMode && !isStaffPlatformModeSidebarEntry(e.backendRouteKey)) return false;
    if (
      !platformMode &&
      resolveStaffNavSidebarSection(e, platformMode) === 'platform'
    ) {
      return false;
    }
    /** Журнал аудита — платформенный контур (СА без org) или platform_staff с org (audit:org_read). */
    if (!platformMode && e.backendRouteKey === '/audit-log') {
      const hasOrgAuditRead = hasPermission(capabilities, 'audit:org_read');
      const isImpersonatingSuper =
        me?.role === 'superadmin' && impersonateOrganizationId != null && impersonateOrganizationId > 0;
      if (!hasOrgAuditRead && !isImpersonatingSuper) return false;
    }
    if (
      !platformMode &&
      isTenantStaffGovernanceKeyedRoute(e.backendRouteKey) &&
      !hasTenantWideStaffGovernanceContourOrSaOrgLift(capabilities, saOrgWideGateCtx)
    ) {
      return false;
    }
    if (
      !platformMode &&
      isTenantOrganizationProfileStaffRouteKey(e.backendRouteKey) &&
      !canAccessTenantOrganizationProfileSurface(capabilities, e.backendRouteKey, saOrgWideGateCtx)
    ) {
      return false;
    }
    if (e.backendRouteKey === '/superadmin/users') {
      if (!canShowSuperadminUsersHubNavItem(capabilities, me, impersonateOrganizationId)) return false;
    } else if (e.backendRouteKey === '/audit-log') {
      if (!isRouteAllowed(capabilities, '/audit-log')) return false;
      if (platformMode && !hasPermission(capabilities, 'platform:audit:view')) return false;
    } else if (!canShowNavItem(capabilities, e)) {
      return false;
    }
    return e.sidebarVisible !== false;
  });
}
